import { useReducer, useEffect, useRef, useState } from "react";
import { ChatUI } from "./ChatUI";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import ChatApi from "../../services/ChatApi";
import ChatStorageService from "../../services/ChatStorageService";
import { Auth } from "../../utils/auth";

const TYPING_MESSAGE_ID = "typing";
const WELCOME_MESSAGE_TEXT =
  "**¡Hola! Soy tu asistente digital 👷**\n\nEstoy aquí para resolver tus dudas sobre el estado de los permisos de trabajo.\n\n**¿Qué información deseas consultar hoy?**";
const BACKEND_NO_RESPONSE_TEXT =
  "En este momento no pude responderte. Intenta nuevamente en unos minutos.";

const messageReducer = (state = [], action) => {
  switch (action.type) {
    case "ADD_MESSAGE":
      return [...state, action.payload];
    case "REMOVE_MESSAGE":
      return state.filter((message) => message.id !== action.payload);
    case "RESET_MESSAGES":
      return [];
    case "SET_MESSAGES":
      return action.payload;
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

const createMessage = (text, sent) => ({
  id: uuidv4(),
  created_at: new Date().toISOString(),
  sent,
  text,
});

const parseJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }

    const tokenParts = token.split(".");
    if (tokenParts.length !== 3 || !tokenParts[1]) {
      return null;
    }

    const base64 = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

export function VirtualAssistant() {
  const [messages, dispatch] = useReducer(messageReducer, []);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const chatEndRef = useRef(null);

  const refreshMessagesFromStorage = () => {
    const userId = getCurrentUserId();

    if (userId) {
      const previousMessages = ChatStorageService.getUserMessages(userId);
      const storedConversationId = ChatStorageService.getUserConversationId(userId);
      setConversationId(storedConversationId);

      if (Array.isArray(previousMessages) && previousMessages.length > 0) {
        dispatch({ type: "SET_MESSAGES", payload: previousMessages });
        return;
      }
    }

    setConversationId(null);

    dispatch({ type: "SET_MESSAGES", payload: [createMessage(WELCOME_MESSAGE_TEXT, false)] });
  };

  const getCurrentUserId = () => {
    const auth = new Auth();
    const user = auth.getUser();

    const userIdFromAuth =
      user?.id || user?.userId || user?.employeeId || user?.oid || user?.sub;
    if (userIdFromAuth) {
      return String(userIdFromAuth);
    }

    try {
      const profileRaw = localStorage.getItem("profile");
      const profile = profileRaw ? JSON.parse(profileRaw) : null;
      const userIdFromProfile =
        profile?.Id ||
        profile?.id ||
        profile?.UserId ||
        profile?.userId ||
        profile?.EmployeeId ||
        profile?.employeeId;

      if (userIdFromProfile) {
        return String(userIdFromProfile);
      }
    } catch (error) {
      // Ignore malformed profile data and continue with token fallback.
    }

    const token = auth.getToken();
    const tokenPayload = parseJwtPayload(token);
    const userIdFromToken = tokenPayload?.oid || tokenPayload?.sub;
    return userIdFromToken ? String(userIdFromToken) : null;
  };

  const saveMessageToStorage = (message, userId) => {
    if (!userId) {
      return;
    }

    ChatStorageService.saveMessage({
      id: message.id,
      userId,
      created_at: message.created_at,
      sent: message.sent,
      text: message.text,
    });
  };

  const addAssistantMessage = (text, userId) => {
    const assistantMessage = createMessage(text, false);
    dispatch({ type: "ADD_MESSAGE", payload: assistantMessage });
    saveMessageToStorage(assistantMessage, userId);
  };

  const addTypingIndicator = () => {
    if (!messages.find((msg) => msg.id === TYPING_MESSAGE_ID)) {
      const typingMessage = createMessage("", false);
      typingMessage.id = TYPING_MESSAGE_ID;
      typingMessage.isTyping = true;
      dispatch({ type: "ADD_MESSAGE", payload: typingMessage });
    }
  };

  const removeTypingIndicator = () => {
    dispatch({ type: "REMOVE_MESSAGE", payload: TYPING_MESSAGE_ID });
  };

  // Load previous messages on mount
  useEffect(() => {
    refreshMessagesFromStorage();

    const intervalId = setInterval(() => {
      refreshMessagesFromStorage();
    }, 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleNewMessage = async (inputMessage) => {
    if (!inputMessage?.text?.trim()) {
      toast.error("No puedes enviar un mensaje vacio.");
      return;
    }

    if (isSending) {
      return;
    }

    setIsSending(true);
    const userId = getCurrentUserId();
    const conversationIdToSend = messages.length === 0 ? null : conversationId || null;

    const userMessage = createMessage(inputMessage.text, true);
    dispatch({ type: "ADD_MESSAGE", payload: userMessage });
    saveMessageToStorage(userMessage, userId);
    addTypingIndicator();

    try {
      const response = await ChatApi.processText(inputMessage.text, conversationIdToSend);
      if (response?.status && response.status !== 200) {
        const errorText =
          response.data?.error ||
          response.data?.message ||
          response.data?.responseText ||
          response.data?.data?.text ||
          "Hubo un error. Por favor, intenta nuevamente.";
        toast.error(errorText);
        addAssistantMessage(BACKEND_NO_RESPONSE_TEXT, userId);
      } else {
        const responseConversationId =
          response?.conversationId || response?.data?.conversationId;
        if (typeof responseConversationId === "string" && responseConversationId.trim()) {
          setConversationId(responseConversationId);
          ChatStorageService.saveUserConversationId(userId, responseConversationId);
        }

        const responseText =
          typeof response === "string"
            ? response
            : response?.responseText ||
              response?.data?.responseText;
        const finalResponseText =
          responseText && String(responseText).trim()
            ? responseText
            : BACKEND_NO_RESPONSE_TEXT;
        addAssistantMessage(finalResponseText, userId);
      }
    } catch (error) {
      console.error("Error al procesar el texto:", error);
      toast.error("No se pudo conectar con el servidor.");
      addAssistantMessage(BACKEND_NO_RESPONSE_TEXT, userId);
    } finally {
      removeTypingIndicator();
      setIsSending(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      <ChatUI
        messages={messages}
        onSendMessage={handleNewMessage}
        isTyping={!!messages.find((msg) => msg.id === TYPING_MESSAGE_ID)}
        isSending={isSending}
        chatEndRef={chatEndRef}
      />
    </Box>
  );
}
